import { useMemo } from 'react';
import { Device, Range, rangeToDate, Trigger } from '@/hooks/useTriggerStream';
import { FlowRiver } from './FlowRiver';
import { buildDeviceColorMap } from './vizPalette';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

interface Props {
  triggers: Trigger[];
  devices: Device[];
  dark: boolean;
}

const WINDOWS: Range[] = ['1h', '24h', '7d', '30d', '1y', '10y'];

export function FlowRiverGallery({ triggers, devices, dark }: Props) {
  const t = useT(visualizeContent).charts;
  const triggerWindows = useMemo(() => {
    const now = Date.now();
    return new Map(WINDOWS.map((range) => {
      const cutoff = rangeToDate(range).getTime();
      return [range, triggers.filter((trigger) => {
        const timestamp = new Date(trigger.created_at).getTime();
        return timestamp >= cutoff && timestamp <= now;
      })];
    }));
  }, [triggers]);

  const colorMap = useMemo(
    () => buildDeviceColorMap([...devices.map((d) => d.device_id), ...triggers.map((t) => t.device_id)]),
    [devices, triggers],
  );

  return (
    <div className="space-y-16">
      {WINDOWS.map((range) => (
        <div key={range}>
          <div className="mb-5 flex items-baseline justify-between gap-4 border-b border-sitefg/10 pb-3">
            <h3 className="text-lg font-light">{t.windows[range]}</h3>
            <span className="font-mono text-[10px] uppercase text-sitefg/40">
              {(triggerWindows.get(range)?.length ?? 0).toLocaleString()}{' '}{t.triggers}
            </span>
          </div>
          <FlowRiver
            triggers={triggerWindows.get(range) ?? []}
            devices={devices}
            range={range}
            colorMap={colorMap}
            showLabels={false}
            showLegend={false}
            dark={dark}
          />
        </div>
      ))}
    </div>
  );
}